import { Component, Input, OnInit } from '@angular/core'
import { Store, select } from '@ngrx/store'
import {
  DataCheckerService,
  DataEntityType,
  Destroyable,
  SaveForm,
  positive,
  untilDestroy,
} from '@tokeet-frontend/tv3-platform'
import {
  AddCustomAttribute,
  EntityAttributeGuard,
  EntityAttributeResponse,
  selectEntityAttributesByEntityId,
  selectEntityAttributesLoaded,
  selectEntityAttributesSaving,
} from '../../store/attrs'
import { FormArray, FormBuilder, FormGroup } from '@angular/forms'
import * as lodash from 'lodash'
import { AttributeDescriptionGuard, selectAttributeDescriptionByEntityType } from '../../store/attr-desc'
import { combineLatest, Subscription } from 'rxjs'
import { map } from 'rxjs/operators'
import { AttributeFieldDef, AttributeFieldType } from '../../store/attr-desc'
import { EntityAttributesConfigOverlayService } from '../entity-attributes-config-overlay/entity-attributes-config-overlay.service'

interface EntityAttributesFormDataItem {
  label: string
  description: string
  type: string
  key: string
  value: any
}

@Component({
  selector: 'app-entity-attributes',
  templateUrl: './entity-attributes.component.html',
  styleUrls: ['./entity-attributes.component.scss'],
})
export class EntityAttributesComponent extends Destroyable implements OnInit {
  _entityId: string = null

  @Input() entityType: DataEntityType
  @Input() set entityId(id: string) {
    this._entityId = id
    setTimeout(() => this.init(), 10)
  }
  get entityId() {
    return this._entityId
  }
  @Input() showActions = false
  @Input() actionsPosition: 'start' | 'center' | 'end' = 'start'

  fieldDefsByKey: Record<string, any> = {}
  isFieldDefsEmpty = true

  dataTypes = AttributeFieldType

  form = this.fb.group({
    attrs: this.fb.array([]),
  })

  get attrsArrayForm() {
    return this.form.get('attrs') as FormArray
  }

  isLoading$ = this.store.pipe(
    select(selectEntityAttributesLoaded),
    map((loaded) => !loaded)
  )
  isSaving$ = this.store.pipe(select(selectEntityAttributesSaving))

  sub: Subscription
  constructor(
    private fb: FormBuilder,
    private store: Store<any>,
    private dataCheckerService: DataCheckerService,
    private entityAttributesConfigOverlay: EntityAttributesConfigOverlayService
  ) {
    super()
    this.dataCheckerService.check([AttributeDescriptionGuard, EntityAttributeGuard])
  }

  ngOnInit(): void {}

  init() {
    if (this.sub) this.sub.unsubscribe()

    this.sub = combineLatest([
      this.store.pipe(select(selectAttributeDescriptionByEntityType(this.entityType))),
      this.store.pipe(select(selectEntityAttributesByEntityId(this.entityId))),
    ])
      .pipe(untilDestroy(this))
      .subscribe(([description, attr]) => {
        this.fieldDefsByKey = lodash.keyBy(description?.attribute_fields, (t) => t.attribute)
        this.isFieldDefsEmpty = lodash.isEmpty(this.fieldDefsByKey)

        this.setForm(description?.attribute_fields || [], attr)
      })
  }

  setForm(fields: AttributeFieldDef[], attrs?: EntityAttributeResponse) {
    fields = lodash.orderBy(fields, [
      (t) => (t.field_type === AttributeFieldType.Boolean ? 1 : 0),
      (t) => lodash.toLower(t.label),
    ])
    this.attrsArrayForm.clear()
    lodash.forEach(fields, (field) => {
      const value = attrs?.attributes?.[field.attribute]
      this.attrsArrayForm.push(
        this.fb.group({
          label: [field.label],
          description: [field.description],
          type: [field.field_type],
          key: [field.attribute],
          value: [value, field.field_type === AttributeFieldType.PositiveNumber ? [positive()] : []],
        })
      )
    })
  }

  onConfig() {
    this.entityAttributesConfigOverlay.open(this.entityType)
  }

  save() {
    this.onSave(this.form)
  }

  @SaveForm()
  onSave(form: FormGroup) {
    const { attrs }: { attrs: EntityAttributesFormDataItem[] } = form.getRawValue()

    const payload = lodash.mapValues(
      lodash.keyBy(attrs, (item) => item.key),
      (t) => t.value
    )

    this.store.dispatch(AddCustomAttribute({ entityType: this.entityType, entityId: this.entityId, payload }))
  }
}
